import { ReactElement, ReactNode } from 'react'
import { Box, Button, Switch, Tooltip } from '@mui/material'
import { MiniTooltipButton } from '@ui2/forms'
import { MaterialSymbol } from '@ui2/icons'
import { StickyFooter } from '@ui2/structure'

export type Results2ListPaneActionsFooterProps = {
  filter3d: boolean
  onFilter3dChange: (next: boolean) => void
  fokusCamera: boolean
  onFokusCameraChange: (next: boolean) => void
  fokusTransparency: boolean
  onFokusTransparencyChange: (next: boolean) => void
  onThreeDTargetBauteile?: () => void
  onThreeDTargetWaende?: () => void
  showBlockpadButton?: boolean
  onBlockpad?: () => void
  blockpadSlot?: ReactNode
  calculateSlot?: ReactNode
  onCalculate?: () => void
  /** Cancels in-flight results loading. Rendered as an "Abbrechen" button when {@link showAbort}. */
  onAbort?: () => void
  /** When true, shows the "Abbrechen" button next to "Berechnen". */
  showAbort?: boolean
  isLoading?: boolean
  disableForm?: boolean
}

const Results2ListPaneActionsFooter = ({
  filter3d,
  onFilter3dChange,
  fokusCamera,
  onFokusCameraChange,
  fokusTransparency,
  onFokusTransparencyChange,
  onThreeDTargetBauteile,
  onThreeDTargetWaende,
  showBlockpadButton = true,
  onBlockpad,
  blockpadSlot,
  calculateSlot,
  onCalculate,
  onAbort,
  showAbort = false,
  isLoading = false,
  disableForm = false,
}: Results2ListPaneActionsFooterProps): ReactElement => (
  <StickyFooter>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: 1.25,
        rowGap: 0.75,
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 1.25,
          rowGap: 0.5,
          flex: '1 1 auto',
          minWidth: 0,
        }}
      >
        <Tooltip title="Filter in 3D anwenden" arrow disableInteractive placement="top">
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Switch
              size="small"
              checked={filter3d}
              onChange={e => onFilter3dChange(e.target.checked)}
            />
            <Box component="span" sx={{ fontSize: 12 }}>
              Filter anwenden
            </Box>
          </Box>
        </Tooltip>
        <Tooltip
          title="Kamera auf das ausgewählte Element ausrichten"
          arrow
          disableInteractive
          placement="top"
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Switch
              size="small"
              checked={fokusCamera}
              onChange={e => onFokusCameraChange(e.target.checked)}
              data-cy="results2-fokus-switch"
            />
            <Box component="span" sx={{ fontSize: 12 }}>
              Fokus
            </Box>
          </Box>
        </Tooltip>
        <Tooltip
          title="Auswahl in 3D isolieren (übrige Bauteile transparent)"
          arrow
          disableInteractive
          placement="top"
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Switch
              size="small"
              checked={fokusTransparency}
              onChange={e => onFokusTransparencyChange(e.target.checked)}
              data-cy="results2-transparency-switch"
            />
            <Box component="span" sx={{ fontSize: 12 }}>
              Transparenz
            </Box>
          </Box>
        </Tooltip>
      </Box>
      {onThreeDTargetBauteile && onThreeDTargetWaende ? (
        <Box sx={{ display: 'inline-flex', gap: 0.5, flexShrink: 0, minWidth: 'max-content' }}>
          <MiniTooltipButton
            onClick={onThreeDTargetBauteile}
            icon={<MaterialSymbol icon="category" size={13} />}
            label="Bauteile"
            tooltip="Bauteile in 3D-Ansicht auswählen"
            tooltipPlacement="top"
          />
          <MiniTooltipButton
            onClick={onThreeDTargetWaende}
            icon={<MaterialSymbol icon="square_foot" size={13} />}
            label="Wände"
            tooltip="Wände in 3D-Ansicht auswählen"
            tooltipPlacement="top"
          />
        </Box>
      ) : null}
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
      {showBlockpadButton ? (
        <Box>
          {blockpadSlot ?? (
            <Button
              variant="outlined"
              size="small"
              sx={{ fontWeight: 500 }}
              startIcon={<MaterialSymbol icon="view_module" size={16} />}
              onClick={onBlockpad}
            >
              Blockpad
            </Button>
          )}
        </Box>
      ) : null}
      <Box>
        {calculateSlot ?? (
          <Button
            variant="contained"
            size="small"
            sx={{ fontWeight: 500 }}
            startIcon={<MaterialSymbol icon="calculate" size={16} />}
            onClick={onCalculate}
            disabled={disableForm || !onCalculate || isLoading}
            data-cy="recalculate-results"
          >
            Berechnen
          </Button>
        )}
      </Box>
      {showAbort && onAbort ? (
        <Box>
          <Button
            variant="outlined"
            size="small"
            sx={{ fontWeight: 500 }}
            endIcon={<MaterialSymbol icon="close" size={16} />}
            onClick={onAbort}
            data-cy="abort-results-loading"
          >
            Abbrechen
          </Button>
        </Box>
      ) : null}
    </Box>
  </StickyFooter>
)

export default Results2ListPaneActionsFooter
